import { PatientAttributeOptions, PatientAttributes } from "./Query/PatientAttributesQuery/PatientAttributesQuery.consts";
import { CallRoutingOptions, CallRoutingAttributes } from "./Query/CallRoutingQuery/CallRoutingQuery.consts";
import { ConversationExperienceOptions, ConversationExperienceAttributes } from "./Query/ConversationExperienceQuery/ConversationExperienceQuery.consts";
import { UserActionsOptions, UserActionsAttributes } from "./Query/UserActionsQuery/UserActionsQuery.consts";

export const PAGE_MAX_HEIGHT = "calc(100vh - 256px)";
export const EMPTY_CONVERSATION_LABEL = "This link has expired";
export const EMPTY_CONVERSATION_LINK_TEXT = "Go to conversations page";
export const COPY_CONVERSATION_LINK_SUCCESS_MESSAGE = "Successfully copied conversation link";
export const COPY_CONVERSATION_ID_SUCCESS_MESSAGE = "Successfully copied conversation ID";
export const COPY_USER_TEXT_SUCCESS_MESSAGE = "Successfully copied user text";

const NO_CONVERSATIONS_FOUND = "No conversations found";

export const CONVERSATIONS_STORAGE = {
  SIDE_PANEL: "conversationSidePanelOpen"
};

// Shared with the analytics reports' saved filters, which present the same save form and the
// same delete confirmation. Re-exported here so existing imports keep their local path.
export {
  DELETE_FILTER_COPY,
  FILTER_NAME_ERROR,
  FILTER_NAME_MAX_LENGTH,
  FILTER_VISIBILITY,
  FILTER_VISIBILITY_LABELS,
  MY_FILTERS_SECTION_LABEL,
  SHARED_FILTERS_SECTION_LABEL
} from "src/components/widgets/filters/savedFilters.consts";

const QueryOperators = {
  AND: {
    key: "AND",
    subtitle: "All filters must match",
    label: "And"
  },
  OR: {
    key: "OR",
    subtitle: "At least one filter must match",
    label: "Or"
  }
};

export const SkillFilters = {
  INCLUDES: {
    key: "INCLUDES",
    label: "Includes"
  },
  DOES_NOT_INCLUDE: {
    key: "DOES_NOT_INCLUDE",
    label: "Does not include"
  }
};

export const GoalCompletionFilters = {
  COMPLETED: {
    key: "COMPLETED",
    label: "Is included"
  },
  NOT_COMPLETED: {
    key: "NOT_COMPLETED",
    label: "Is not included"
  }
};

export const IsIsNotFilter = {
  IS: {
    key: "IS",
    label: "Is"
  },
  IS_NOT: {
    key: "IS_NOT",
    label: "Is not"
  }
};

export const IsOneOfFilter = {
  IS_ONE_OF: {
    key: "IS_ONE_OF",
    label: "Is one of"
  },
  IS_NOT_ONE_OF: {
    key: "IS_NOT_ONE_OF",
    label: "Is not one of"
  }
};

export const MULTI_SELECT_FILTERS = Object.values(IsOneOfFilter).map((filter) => filter.key);

export const Filters = {
  ...IsIsNotFilter,
  CONTAINS: {
    key: "CONTAINS",
    label: "Contains"
  },
  DOES_NOT_CONTAIN: {
    key: "DOES_NOT_CONTAIN",
    label: "Does not contain"
  }
};

const QueryEventTypes = {
  USER_UTTERANCE: {
    key: "USER_UTTERANCE",
    label: "User Utterance"
  },
  BOT_UTTERANCE: {
    key: "BOT_UTTERANCE",
    label: "Agent Utterance"
  },
  SKILL: {
    key: "SKILL",
    label: "Skill"
  },
  SITE: {
    key: "SITE",
    label: "Site"
  },
  GOAL_COMPLETION: {
    key: "GOAL_COMPLETION",
    label: "Goal Completion"
  },
  LANGUAGE: {
    key: "LANGUAGE",
    label: "Language"
  },
  PATIENT_ATTRIBUTES: {
    key: "PATIENT_ATTRIBUTES",
    label: "Patient Attributes"
  },
  CALL_ROUTING: {
    key: "CALL_ROUTING",
    label: "Call Routing"
  },
  CONVERSATION_EXPERIENCE: {
    key: "CONVERSATION_EXPERIENCE",
    label: "Conversation Experience"
  },
  USER_ACTIONS: {
    key: "USER_ACTIONS",
    label: "User Actions"
  }
};

const ExtraQueryEventTypes = {
  ...QueryEventTypes,
  EVENTS_AND_DATA: {
    key: "EVENTS_AND_DATA",
    label: "Events & Data"
  }
};

const Mode = {
  ALL: {
    key: "ALL",
    label: "All"
  },
  LIVE: {
    key: "LIVE",
    label: "live"
  },
  TEST: {
    key: "TEST",
    label: "test"
  }
};

const EngagedFilter = {
  ENGAGED: {
    key: true,
    label: "Engaged"
  },
  UNENGAGED: {
    key: false,
    label: "Unengaged"
  }
};

const initialUserUtteranceQueryState = {
  operator: QueryOperators.AND.key,
  eventType: QueryEventTypes.USER_UTTERANCE.key,
  type: QueryEventTypes.USER_UTTERANCE.key,
  key: "utterance",
  filter: Filters.IS.key,
  value: ""
};

const initialBotUtteranceQueryState = {
  operator: QueryOperators.AND.key,
  eventType: QueryEventTypes.BOT_UTTERANCE.key,
  type: QueryEventTypes.BOT_UTTERANCE.key,
  key: "text",
  filter: Filters.IS.key,
  value: ""
};

const initialSkillQueryState = {
  operator: QueryOperators.AND.key,
  eventType: QueryEventTypes.SKILL.key,
  type: QueryEventTypes.SKILL.key,
  filter: SkillFilters.INCLUDES.key,
  value: ""
};
const initialSiteQueryState = {
  operator: QueryOperators.AND.key,
  eventType: QueryEventTypes.SITE.key,
  type: QueryEventTypes.SITE.key,
  key: "site",
  filter: IsIsNotFilter.IS.key,
  value: ""
};

const initialGoalCompletionQueryState = {
  operator: QueryOperators.AND.key,
  eventType: QueryEventTypes.GOAL_COMPLETION.key,
  type: QueryEventTypes.GOAL_COMPLETION.key,
  key: "goal_completion",
  filter: GoalCompletionFilters.COMPLETED.key,
  value: ""
};

const initialPatientAttributesQueryState = {
  operator: QueryOperators.AND.key,
  eventType: QueryEventTypes.PATIENT_ATTRIBUTES.key,
  subType: PatientAttributeOptions.AGE_GROUP,
  type: PatientAttributes[PatientAttributeOptions.AGE_GROUP].type,
  key: PatientAttributes[PatientAttributeOptions.AGE_GROUP].key,
  filter: IsIsNotFilter.IS.key,
  value: ""
};

const initialCallRoutingQueryState = {
  operator: QueryOperators.AND.key,
  eventType: QueryEventTypes.CALL_ROUTING.key,
  subType: CallRoutingOptions.HANDOFF.key,
  type: CallRoutingAttributes[CallRoutingOptions.HANDOFF.key].type,
  filter: IsIsNotFilter.IS.key,
  value: ""
};

const initialConversationExperienceQueryState = {
  operator: QueryOperators.AND.key,
  eventType: QueryEventTypes.CONVERSATION_EXPERIENCE.key,
  subType: ConversationExperienceOptions.LONG_SILENCE,
  type: ConversationExperienceAttributes[ConversationExperienceOptions.LONG_SILENCE].type,
  filter: IsIsNotFilter.IS.key,
  value: ""
};

const initialUserActionsQueryState = {
  operator: QueryOperators.AND.key,
  eventType: QueryEventTypes.USER_ACTIONS.key,
  subType: UserActionsOptions.RESULT_CLICK,
  type: UserActionsAttributes[UserActionsOptions.RESULT_CLICK].type,
  key: UserActionsAttributes[UserActionsOptions.RESULT_CLICK].key,
  filter: IsIsNotFilter.IS.key,
  value: ""
};

const initialLanguageQueryState = {
  operator: QueryOperators.AND.key,
  eventType: QueryEventTypes.LANGUAGE.key,
  type: "SPEECH_LANGUAGE",
  key: "lang",
  filter: IsIsNotFilter.IS.key,
  value: ""
};

const initialEventQueryState = {
  operator: QueryOperators.AND.key,
  eventType: ExtraQueryEventTypes.EVENTS_AND_DATA.key,
  type: "",
  value: "",
  filter: Filters.CONTAINS.key
};

const queryFilterOptions = {
  [QueryEventTypes.USER_UTTERANCE.key]: Filters,
  [QueryEventTypes.BOT_UTTERANCE.key]: Filters,
  [QueryEventTypes.SKILL.key]: SkillFilters,
  [QueryEventTypes.SITE.key]: IsIsNotFilter,
  [QueryEventTypes.GOAL_COMPLETION.key]: GoalCompletionFilters,
  [QueryEventTypes.LANGUAGE.key]: IsIsNotFilter
};

const SPECIAL_EVENT_QUERIES = [
  QueryEventTypes.PATIENT_ATTRIBUTES.key,
  QueryEventTypes.CALL_ROUTING.key,
  QueryEventTypes.CONVERSATION_EXPERIENCE.key,
  QueryEventTypes.USER_ACTIONS.key
];

export {
  NO_CONVERSATIONS_FOUND,
  QueryEventTypes,
  ExtraQueryEventTypes,
  Mode,
  EngagedFilter,
  initialEventQueryState,
  initialPatientAttributesQueryState,
  initialUserUtteranceQueryState,
  initialBotUtteranceQueryState,
  initialSkillQueryState,
  initialLanguageQueryState,
  initialGoalCompletionQueryState,
  initialSiteQueryState,
  initialCallRoutingQueryState,
  initialConversationExperienceQueryState,
  initialUserActionsQueryState,
  queryFilterOptions,
  QueryOperators,
  SPECIAL_EVENT_QUERIES
};
